import React from 'react'
import {
  MetricCard,
  StatsCard,
  Divider,
  DemographicsSection,
  DescriptionAndContactInfo,
} from 'components/NewCompanyPage/Sidebar/shared'
import {
  formatInteger,
  formatAddress,
  usdFormatterOrFallback,
} from 'utils/formatting'
import { toLegacyAddressInfo } from 'services/hospital'
import { GetHospitalQueryResponse } from 'services/generated/types/GetHospital'
import { GoogleMapsLink } from '../utils/GoogleMapsLink'
import { BuildCompanyTypeContentOptions, CompanyTypePageContent } from './types'

/**
 * Shared hospital content derivation used by both `NewHospitalPage` (desktop)
 * and `MobileHospitalPage`. See {@link CompanyTypePageContent}.
 */
export function buildHospitalPageContent(
  data: GetHospitalQueryResponse | undefined,
  { onViewDoors }: BuildCompanyTypeContentOptions = {}
): CompanyTypePageContent {
  const hospital = data?.hospital

  const operationalStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Provider Number',
      value: hospital?.provider_number || 'N/A',
    },
    {
      label: 'Hospital Beds',
      value: hospital?.total_hospital_beds
        ? formatInteger(hospital.total_hospital_beds)
        : 'N/A',
    },
    {
      label: 'Meals Per Day',
      value: hospital?.meals_per_day
        ? formatInteger(hospital.meals_per_day)
        : 'N/A',
    },
    {
      label: 'Hospital Days',
      value: hospital?.total_hospital_days
        ? formatInteger(hospital.total_hospital_days)
        : 'N/A',
    },
    {
      label: 'Data Year',
      value: hospital?.year ? hospital.year.toString() : 'N/A',
    },
  ]

  const financialStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Cafeteria Expenditures',
      value: usdFormatterOrFallback(hospital?.cafeteria, 'N/A'),
    },
    {
      label: 'Patient Meal Expenditures',
      value: usdFormatterOrFallback(hospital?.dietary, 'N/A'),
    },
    {
      label: 'Contracted Patient Meal Expenditures',
      value: usdFormatterOrFallback(hospital?.dietary_under_contract, 'N/A'),
    },
  ]

  const summaryContent: React.ReactNode[] = []

  summaryContent.push(
    <DescriptionAndContactInfo
      key="description-contact"
      googlePlaceUrl={hospital?.google_place_url || undefined}
      address={
        formatAddress({
          street: hospital?.street_addr,
          city: hospital?.city,
          state: hospital?.state,
          zip: hospital?.zip_code,
        }) ?? undefined
      }
    />
  )

  if (onViewDoors && data?.doors_count && data.doors_count > 0) {
    summaryContent.push(
      <MetricCard
        key="doors"
        value={data.doors_count}
        label="Door Count"
        actionText="View Doors"
        onActionClick={onViewDoors}
      />
    )
  }

  summaryContent.push(
    <StatsCard key="operational-stats" stats={operationalStats} />,
    <Divider key="divider-1" />,
    <StatsCard key="financial-stats" stats={financialStats} />
  )

  const hospitalLocation = hospital?.street_addr
    ? `${hospital.city}, ${hospital.state} - USA`
    : 'N/A'

  const locationInfoStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Full Address',
      value: <GoogleMapsLink address={hospital?.street_addr} />,
    },
    { label: 'PO Box', value: hospital?.po_box || 'N/A' },
    { label: 'ZIP Code', value: hospital?.zip_code || 'N/A' },
    { label: 'State', value: hospital?.state || 'N/A' },
    { label: 'City', value: hospital?.city || 'N/A' },
    { label: 'Website', value: hospital?.website_url || 'N/A' },
  ]

  const locationDetailsContent: React.ReactNode[] = [
    <MetricCard
      key="hospital-location"
      value={hospitalLocation}
      label="Hospital Location"
      variant="full-width"
    />,
    <StatsCard key="location-info" stats={locationInfoStats} />,
  ]

  const legacyAddressInfo = toLegacyAddressInfo(hospital?.address_info)
  if (legacyAddressInfo) {
    locationDetailsContent.push(
      <Divider key="divider-2" />,
      <DemographicsSection
        key="zipcode-demographics"
        addressInfo={legacyAddressInfo}
      />
    )
  }

  return {
    documentTitle: `${hospital?.hospital_name || 'Not Found'} (Hospital) - First Bite`,
    header: {
      name: hospital?.hospital_name ?? '',
      domain: hospital?.domain || undefined,
      logoUrl: undefined,
    },
    contactCompanyId: data?.id,
    doorsCount: data?.doors_count ?? 0,
    summaryContent,
    locationDetailsContent,
    breadcrumbRoot: 'Healthcare - Hospital',
    breadcrumbTitle: 'Hospital',
    categoryInfo: {
      categorySlug: 'non-commercial',
      subCategorySlug: 'healthcare-hospitals',
    },
    companyTypeSlug: 'healthcare-hospitals',
    tableTypeSlug: 'healthcare-hospitals',
    modalSubCategory: {
      name: 'Healthcare - Hospitals',
      slug: 'healthcare-hospitals',
      description: 'Healthcare - Hospitals',
    },
  }
}
